import { Injectable } from '@angular/core';

/**
 * Service for normalizing HTML content for email rendering.
 * Email clients often strip <div> and collapse whitespace, which can turn multi-line content into a single line.
 * This service ensures line breaks survive email client sanitization by converting block boundaries to <br> tags.
 */
@Injectable({
  providedIn: 'root'
})
export class    EmailHtmlNormalizerService {

  constructor() { }

  /**
   * Normalizes HTML for email clients by:
   * 1. Converting <p> tags to <div> to avoid email client paragraph spacing
   * 2. Converting newline characters in text nodes to explicit <br> elements
   * 3. Converting empty blocks to <br> tags
   * 4. Normalizing list spacing
   * 5. Ensuring <br> exists between consecutive simple line blocks
   * 
   * @param input The HTML string to normalize
   * @returns Normalized HTML string safe for email rendering
   */
  normalizeHtmlForEmail(input: string): string {
    let html = (input || '').toString();
    if (!html.trim()) return html;

    // If editor ever provides plain text with newlines, convert to <br>.
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(html);
    if (!looksLikeHtml) {
      return html.replace(/\r\n|\r|\n/g, '<br>');
    }

    // Robust path: parse HTML and normalize line breaks in the DOM.
    // Many email gateways strip <div>/<p> or collapse raw newlines, so we ensure <br> nodes exist.
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const body = doc.body;

      // Normalize: some email clients add margins/spacing for <p> and can create
      // visually "empty" gaps. Convert all <p> to <div> while preserving attributes
      // and children.
      try {
        const paragraphs = Array.from(body.querySelectorAll('p')) as HTMLParagraphElement[];
        for (const p of paragraphs) {
          const div = doc.createElement('div');
          try {
            for (const attr of Array.from(p.attributes)) {
              div.setAttribute(attr.name, attr.value);
            }
          } catch (e) {
            // ignore
          }

          // Move children to preserve DOM nodes (incl. <br>, links, spans, etc.)
          while (p.firstChild) {
            div.appendChild(p.firstChild);
          }

          try {
            p.replaceWith(div);
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }

      // 1) Convert newline characters in text nodes into explicit <br> elements.
      const textNodes: Text[] = [];
      const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const t = walker.currentNode as Text;
        if ((t.nodeValue || '').match(/\r|\n/)) textNodes.push(t);
      }

      for (const t of textNodes) {
        const raw = (t.nodeValue || '');
        const parts = raw.split(/\r\n|\r|\n/);
        if (parts.length <= 1) continue;

        const frag = doc.createDocumentFragment();
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          if (part) frag.appendChild(doc.createTextNode(part));
          if (i < parts.length - 1) frag.appendChild(doc.createElement('br'));
        }
        try {
          t.parentNode?.replaceChild(frag, t);
        } catch (e) {
          // ignore
        }
      }

 

      // Many email clients apply default margins/padding to lists.
      // Normalize <ul>/<ol> spacing so templates look consistent.
      try {
        const lists = Array.from(body.querySelectorAll('ul, ol')) as HTMLElement[];
        for (const list of lists) {
          try {
            const existing = (list.getAttribute('style') || '').trim();
            const reset = 'margin:0;padding-left:18px;';
            // Keep existing styles, but ensure our reset is applied.
            // (padding-left kept non-zero so bullets remain indented.)
            list.setAttribute('style', existing ? `${existing};${reset}` : reset);
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }

      // 3) Ensure <br> exists between consecutive simple line blocks (<div>),
      // so line breaks survive even if the email client strips block semantics.
      const isSimpleLineBlock = (node: Element): boolean => {
        if (!node) return false;
        const tag = node.tagName.toUpperCase();
        if (tag !== 'DIV') return false;
        // If it contains complex block structures, don't treat it as a simple line.
        return !node.querySelector('table,ul,ol,blockquote,pre,h1,h2,h3,h4,h5,h6');
      };

      const isWhitespaceTextNode = (n: Node | null): boolean => {
        try {
          if (!n || n.nodeType !== Node.TEXT_NODE) return false;
          return ((n.nodeValue || '').replace(/\u00a0/g, ' ').trim().length === 0);
        } catch (e) {
          return false;
        }
      };

      const lastMeaningfulChild = (el: Element): Node | null => {
        try {
          let n: Node | null = el.lastChild;
          while (n && isWhitespaceTextNode(n)) n = n.previousSibling;
          return n;
        } catch (e) {
          return el.lastChild;
        }
      };

      const firstMeaningfulChild = (el: Element): Node | null => {
        try {
          let n: Node | null = el.firstChild;
          while (n && isWhitespaceTextNode(n)) n = n.nextSibling;
          return n;
        } catch (e) {
          return el.firstChild;
        }
      };

      const hasTrailingBr = (el: Element): boolean => {
        const n = lastMeaningfulChild(el);
        return !!(n && n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toUpperCase() === 'BR');
      };

      const hasLeadingBr = (el: Element): boolean => {
        const n = firstMeaningfulChild(el);
        return !!(n && n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toUpperCase() === 'BR');
      };

      const ensureBrBetweenSiblings = (container: Element, insertBetweenSiblings: boolean) => {
        let child = container.firstChild;
        while (child) {
          const next = child.nextSibling;

          if (child.nodeType === Node.ELEMENT_NODE) {
            const el = child as Element;
            const tag = el.tagName.toUpperCase();
            
            // Recurse to handle nested wrappers some editors produce.
            try { ensureBrBetweenSiblings(el, true); } catch (e) {}

            if (insertBetweenSiblings && isSimpleLineBlock(el) && next) {
              // Check if next sibling is already a <br> tag
              const nextIsBr =
                next.nodeType === Node.ELEMENT_NODE &&
                (next as Element).tagName.toUpperCase() === 'BR';
              
              // Check if there's whitespace followed by a <br>
              let hasBrAfterWhitespace = false;
              if (!nextIsBr && next && next.nodeType === Node.TEXT_NODE) {
                const afterWhitespace = next.nextSibling;
                hasBrAfterWhitespace = !!(afterWhitespace && 
                  afterWhitespace.nodeType === Node.ELEMENT_NODE &&
                  (afterWhitespace as Element).tagName.toUpperCase() === 'BR' &&
                  isWhitespaceTextNode(next));
              }
              
              // Avoid inserting a double break when the current block already ends with <br>
              // or the next block begins with <br> (common contenteditable artifacts).
              const nextEl = (next && next.nodeType === Node.ELEMENT_NODE ? (next as Element) : null);
              const avoidDouble = hasTrailingBr(el) || (nextEl ? hasLeadingBr(nextEl) : false) || hasBrAfterWhitespace;
              
              if (!nextIsBr && !avoidDouble) {
                try {
                  container.insertBefore(doc.createElement('br'), next);
                } catch (e) {}
              }
            }
          }

          child = next;
        }
      };

      // Do not automatically inject <br> tags between divs.
      // Preserve the existing HTML structure as-is to avoid unwanted line breaks.
      // ensureBrBetweenSiblings(body, false);

      // Compose preview (contenteditable) can visually collapse top-level <br> runs.
      // To make explicit blank lines show reliably, wrap top-level <br> into a block.
      // This keeps list structure intact while improving preview fidelity.
      try {
        const children = Array.from(body.childNodes);
        for (const n of children) {
          if (n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toUpperCase() === 'BR') {
            const wrap = doc.createElement('div');
            wrap.appendChild(doc.createElement('br'));
            try {
              (n as Element).replaceWith(wrap);
            } catch (e) {
              // ignore
            }
          }
        }
      } catch (e) {
        // ignore
      }

      // Do NOT flatten the DOM into a single <br>-joined string here.
      // Flattening breaks list/block structure and can produce the "all inline + <br>" format.
      // Returning body.innerHTML preserves <ul>/<li>/<div> blocks for better email rendering.
      return body.innerHTML;
    } catch (e) {
      // Fallback: conservative regex boundary insertion.
      try {
        // Convert paragraph tags to divs to avoid paragraph spacing in email clients.
        html = html.replace(/<\s*p(\b[^>]*)>/gi, '<div$1>');
        html = html.replace(/<\s*\/\s*p\s*>/gi, '</div>');
        html = html.replace(/<\/div>\s*(?!<br\s*\/?>)\s*<div\b/gi, '</div><br><div');
      } catch (e2) {}
      return html;
    }
  }
}
